import { useCallback } from 'react';

import { ensureDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { resolvableServiceUsageForm2ApiMapper } from '../../ResolvableServiceUsage/mappers/resolvable-service-usage.form2api.mapper';
import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../../ResolvableServiceUsage/fragments/resolvable-service-usage.fragment';
import type { ResolvableServiceUsageFormTab } from '../../ResolvableServiceUsage/enums/tab.enum';
import type { ResolvableServiceFormTab } from '../../ResolvableService/enums/tab.enum';

import type { UpdateMutation } from '../types/props.type';

import { useFindResolvableServiceUsageIndexById } from './useFindIndexById.hook';

interface Params {
  readonly directMutation?: UpdateMutation;
  readonly formContext?: string;
  readonly handleUpdateSuccess: () => void;
  readonly serviceUsageId?: Id | null;
  readonly serviceUsagesGroupId: Id;
  readonly serviceUserId: Id;
}

interface FormikArrayActions {
  readonly updateInForm: (
    index: number,
    value: Readonly<ResolvableServiceUsageForm_ResolvableServiceUsage>,
  ) => void;
}

export const useUpdateResolvableServiceUsageSubmit = ({
  directMutation,
  formContext,
  handleUpdateSuccess,
  serviceUsageId: pServiceUsageId,
  serviceUsagesGroupId,
  serviceUserId,
}: Readonly<Params>) => {
  const findIndexById = useFindResolvableServiceUsageIndexById({ formContext });

  const handleSubmitFactory = useCallback(
    ({ updateInForm }: Readonly<FormikArrayActions>) =>
      ({
        values,
        memos,
        formikHelpers: { setSubmitting },
        setWizardError,
      }: Readonly<
        WizardSubmitHandlerParams<
          ResolvableServiceUsageForm_ResolvableServiceUsage,
          ResolvableServiceUsageFormTab | ResolvableServiceFormTab
        >
      >) => {
        const { serviceUsageId } = ensureDefined({
          serviceUsageId: pServiceUsageId,
        });

        setWizardError(undefined);

        const onError = (error: unknown) => {
          setWizardError({ messages: userFriendlyErrors(error as BackError) });
          setSubmitting(false);
        };

        const onSuccess = () => {
          const index = findIndexById(serviceUsageId);

          if (index === -1) {
            setWizardError({ messages: ["La prestation n'existe pas"] });
            setSubmitting(false);
            return;
          }

          updateInForm(index, values);

          handleUpdateSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              serviceUsageId,
              serviceUsagesGroupId,
              serviceUserId,
              update: resolvableServiceUsageForm2ApiMapper(values),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [
      directMutation,
      findIndexById,
      serviceUsagesGroupId,
      serviceUserId,
      handleUpdateSuccess,
      pServiceUsageId,
    ],
  );

  return { handleSubmitFactory };
};
